<template>
  <div class="gmf-customer-attribute-editor gmf-context" v-if="state.profile">
    <div class="gmf-customer-settings-buttons" v-if="showCustomerSettings">
      <template v-if="editableAvatar">
        <h3 class="gmf-subheader">{{ tcs('avatar') }}</h3>
        <div class="gmf-customer-settings-button">
          <Avatar :customer="state.profile" disableOnlineIndicator disablePopout disablePopup />
          <button class="gmf-btn gmf-btn-sm gmf-btn-primary" @click="customizeAvatar">{{ tcs('edit') }}</button>
          <button v-if="showTokenShop" class="gmf-btn gmf-btn-sm gmf-btn-primary gmf-btn-token" @click="openTokenShop"><img src="https://cdn.gamifiera.com/static/global/mpdApy6mw2IzkYwj.png" class="gmf-token" />{{ tpt('token_shop') }}</button>
        </div>
      </template>
      <template v-if="enabledSkins">
        <h3 class="gmf-subheader">{{ tcs('skin') }}</h3>
        <div class="gmf-customer-settings-button">
          <div :class="darkSkinClass">
            <div class="gmf-customer-settings-skin" :style="computedSkinStyle" />
          </div>
          <button class="gmf-btn gmf-btn-sm gmf-btn-primary" :disabled="!(profileSkin.image_url || profileSkin.imageUrl)" @click="openSkinSelector">{{ tcs('edit') }}</button>
        </div>
      </template>
    </div>

    <div style="padding-bottom: var(--gmf-p-5);" v-if="showActivePills && hasAttributes">
      <CustomerAttributePills :attributes="newAttributes" :maxLines="2" />
    </div>

    <div class="gmf-customer-attribute-editor-section-list">
      <div v-for="attr in editableTypes" :key="attr.name" class="gmf-customer-attribute-editor-section">
        <h3 class="gmf-subheader">{{ t('customer_attributes.' + attr.name + '.title') || attr.name }}</h3>
        <DropdownSelect
          v-if="attr.type === 'singleStringOption'"
          :filterable="attr.params.allowedValues && attr.params.allowedValues.length > 10"
          :placeholder="t('customer_attributes.' + attr.name + '.placeholder') || t('customer_attributes_editor.placeholder')"
          :modelValue="newAttributes[attr.name] === null ? null : (newAttributes[attr.name] || attributes[attr.name] || null)"
          @update:modelValue="v => onUpdate(attr, v)"
          :options="[{
            value: null,
            label: t('customer_attributes_editor.unset_attribute'),
          }, ...attr.params.allowedValues.map(v=>({
            value: v,
            label: t('customer_attributes.' + attr.name + '.values.' + v) || v,
          }))]"
        />
        <DropdownSelect
          v-else-if="attr.type === 'multipleStringOption'"
          :filterable="true"
          :multiple="true"
          :placeholder="t('customer_attributes.' + attr.name + '.placeholder') || t('customer_attributes_editor.placeholder')"
          :modelValue="newAttributes[attr.name] || attributes[attr.name] || null"
          @update:modelValue="v => onUpdate(attr, v)"
          :options="[{
            value: null,
            label: t('customer_attributes_editor.unset_attribute'),
          }, ...attr.params.allowedValues.map(v=>({
            value: v,
            label: t('customer_attributes.' + attr.name + '.values.' + v) || v,
          }))]"
        />
        <TextInput
          v-else-if="attr.type === 'string'"
          :placeholder="t('customer_attributes.' + attr.name + '.placeholder') || t('customer_attributes_editor.placeholder')"
          :modelValue="getAttributeValue(attr.name)"
          @update:modelValue="v => onUpdate(attr, v)"
        />
        <TextArea
          v-else-if="attr.type === 'multilineString'"
          :placeholder="t('customer_attributes.' + attr.name + '.placeholder') || t('customer_attributes_editor.placeholder')"
          :modelValue="getAttributeValue(attr.name)"
          @update:modelValue="v => onUpdate(attr, v)"
        />
        <StringListInput
          v-else-if="attr.type === 'stringList'"
          :placeholder="t('customer_attributes.' + attr.name + '.placeholder') || t('customer_attributes_editor.placeholder')"
          :maxItems="attr.params.maxItems"
          :maxItemLength="attr.params.maxLength"
          :modelValue="getAttributeValue(attr.name)"
          @update:modelValue="v => onUpdate(attr, v)"
        />
        <MultiSelect
          v-else-if="attr.type === 'NOT_USED'"
          :modelValue="getAttributeValue(attr.name)"
          @update:modelValue="v => onUpdate(attr, v)"
          :options="attr.params.allowedValues.map(v=>({
            value: v,
            label: t('customer_attributes.' + attr.name + '.values.' + v) || v,
          }))"
        />
        <DateListInput
          v-else-if="attr.type === 'dateList'"
            :placeholder="t('customer_attributes.' + attr.name + '.placeholder') || t('customer_attributes_editor.placeholder')"
            :maxItems="attr.params.maxItems"
            :modelValue="getAttributeValue(attr.name)"
            @update:modelValue="v => onUpdate(attr, v)"
        />
        <DateInput
          v-else-if="attr.type === 'date'"
            :placeholder="t('customer_attributes.' + attr.name + '.placeholder') || t('customer_attributes_editor.placeholder')"
            :modelValue="getAttributeValue(attr.name)"
            @update:modelValue="v => onUpdate(attr, v)"
        />
        <div v-else>Unknown type <i>{{ attr.type }}</i></div>
      </div>

      <div class="gmf-customer-attribute-editor-section" v-if="! hideIntegritySettings">
        <h3 class="gmf-subheader" v-text="t('profile_settings.header')" />

        <div class="gmf-multi-select">
          <label @click.stop>
            <Checkbox v-model="publicProfile" />
            <span v-text="t('profile_settings.public_profile')" />
          </label>
        </div>
      </div>

    </div>
    <div v-if="showSaveButton && !autoSaveEnabled" class="gmf-customer-attribute-editor-footer">
      <button class="gmf-btn gmf-btn-primary" :class="{ 'gmf-invisible': ! hasChanges }" @click="save" :disabled="! hasChanges">{{ t('customer_attributes_editor.save') }}</button>
    </div>
  </div>
</template>

<style>
.gmf-customer-attribute-editor {
  width: 500px;
  max-width: 100%;
  height: 100%;
}
.gmf-customer-attribute-editor-section-list {
  min-height: 100%;
}
.gmf-customer-attribute-editor .gmf-dropdown-select {
  width: 100%;
  display: block;
}
.gmf-customer-attribute-editor .gmf-level-modal-preview {
  padding-bottom: var(--gmf-p-2);
}
.gmf-customer-attribute-editor .gmf-profile-level-container {
  gap: var(--gmf-p-3);
}
.gmf-customer-attribute-editor-section {
  padding-bottom: var(--gmf-p-5);
}
.gmf-customer-attribute-editor-section:last-child {
  padding-bottom: 0;
}
.gmf-customer-attribute-editor-footer {
  padding-top: var(--gmf-p-5);
  text-align: center;
  position: sticky;
  bottom: 0;
  pointer-events: none;
  z-index: 999;
}
.gmf-customer-attribute-editor-footer .gmf-btn {
  margin-left: var(--gmf-p-3);
  margin-right: var(--gmf-p-3);
  pointer-events: all;
}
.gmf-customer-attribute-editor-footer .gmf-btn.gmf-invisible {
  pointer-events: none;
}
.gmf-customer-attribute-editor .gmf-level-row {
  margin-bottom: var(--gmf-p-3);
}

/* non-sticky footer in standalone widget */
.gmf-widget-customer-attribute-editor .gmf-customer-attribute-editor-footer {
  position: initial;
}
.gmf-customer-settings-buttons {
  display: flex;
  flex-direction: column;
}
.gmf-btn-token {
  display: flex;
  align-items: center;
  gap: var(--gmf-p-2);
}
.gmf-btn-token .gmf-token {
  margin-top: -1px;
}
.gmf-customer-settings-buttons .gmf-avatar {
  width: 48px;
  height: 48px;
  background-color: var(--gmf-col-white);
}
.gmf-customer-settings-button .gmf-customer-settings-skin {
  display: block;
  width: 48px;
  height: 48px;
  border-radius: var(--gmf-rounding);
  background-size: 150%;
  background-position: center;
}
.gmf-customer-settings-buttons .gmf-btn {
  height: fit-content;
}
.gmf-customer-settings-button {
  flex-wrap: wrap;
  display: flex;
  gap: var(--gmf-p-4);
  align-items: center;
  padding-bottom: var(--gmf-p-5);
}
.gmf-customer-settings-button .gmf-btn-sm {
  padding: 8px 14px !important;
}
.gmf-customer-settings-buttons .gmf-dark {
  background: black;
  border-radius: var(--gmf-rounding);
}
</style>

<script>
import callApi from '@/utils/callApi';
import DropdownSelect from '@/components/DropdownSelect';
import MultiSelect from '@/components/MultiSelect';
import TextInput from '@/components/input/TextInput';
import TextArea from '@/components/input/TextArea';
import StringListInput from '@/components/input/StringListInput';
import CustomerAttributePills from '@/components/CustomerAttributePills';
import state from '@/state';
import Checkbox from '@/components/Checkbox';
import DateListInput from '@/components/input/DateListInput';
import DateInput from '@/components/input/DateInput';
import debounce from '@/utils/debounce';

import Avatar from '@/components/Avatar';
import tooltip from '@/utils/tooltip';
import openInModal from '@/legacy/avatar-configurator/openInModal';
import openAvatarConfigurator from '@/components/AvatarConfigurator/openAvatarConfigurator';
import openProfileSkinSelectorModal from '@/components/ProfileSkinSelector/openProfileSkinSelectorModal';
import openTokenShop from '@/components/TokenShop/openTokenShop';

const tcs = state.t.prefix('customer_settings');
const tpt = state.t.prefix('profile_tabs');

export default {
  components: {
    DropdownSelect,
    TextInput,
    TextArea,
    StringListInput,
    MultiSelect,
    CustomerAttributePills,
    Checkbox,
    DateListInput,
    DateInput,
    Avatar,
  },
  props: {
    showCustomerSettings: {
      Type: Boolean,
      default: true,
    },
    showSaveButton: {
      Type: Boolean,
      default: true,
    },
    showActivePills: {
      Type: Boolean,
      default: false,
    },
    hideIntegritySettings: {
      Type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      state,
      types: [],
      attributes: {},
      newAttributes: {},
      publicProfile: state.profile?.publicProfile,
    };
  },
  computed: {
    debouncedSave () {
      return debounce(this.save, 800);
    },
    autoSaveEnabled () {
      return state.merchantConfig.widgets?.attributes?.autoSave;
    },
    editableTypes () {
      return this.types.filter(t => {
        return t.isCustomerEditable;
      });
    },
    hasChanges () {
      if (this.publicProfile !== state.profile.publicProfile) {
        return true;
      }
      if (Object.keys(this.changedAttributes).length > 0) {
        return true;
      }
      return false;
    },
    hasAttributes () {
      return Object.keys(this.newAttributes).length > 0;
    },
    changedAttributes () {
      const changed = {};
      for (const key in this.newAttributes) {
        if (JSON.stringify(this.newAttributes[key]) !== JSON.stringify(this.attributes[key])) {
          changed[key] = this.newAttributes[key];
        }
      }
      return changed;
    },
    editableAvatar() {
      return state.merchantConfig.avatars.enabled === 'enabled' || state.merchantConfig.avatars.enabled === '3D';
    },
    enabledSkins() {
      return state.merchantConfig.widgets?.skins?.enabled;
    },
    use3DAvatar() {
      return state.merchantConfig.avatars.enabled === '3D';
    },
    profileSkin() {
      return state.profile.profileSkin || {};
    },
    darkSkinClass() {
      return { 'gmf-dark': this.profileSkin.dark };
    },
    computedSkinStyle() {
      if (this.hasNoSkin) {
        return {
          border: "dashed 2px var(--gmf-col-darkgray)",
        }
      }

      return {
        backgroundImage: 'url('+(this.profileSkin.image_url || this.profileSkin.imageUrl)+')',
        opacity: this.profileSkin.opacity,
      }
    },
    hasNoSkin() {
      return !state.profile.profileSkin?.uuid;
    },
    showTokenShop() {
      return state.merchantConfig.tokenShop?.enabled;
    },
  },
  methods: {
    getAttributeValue (name) {
      if (name in this.newAttributes) {
        return this.newAttributes[name];
      }

      return this.attributes[name] || null
    },
    t: state.t,
    tcs,
    tpt,
    onUpdate(attr, value) {
      this.newAttributes[attr.name] = value;

      if (this.autoSaveEnabled) {
        this.debouncedSave();
      }
    },
    async save () {
      if (Object.keys(this.changedAttributes).length > 0) {
        await callApi('PATCH', '/v1/client/my-customer-attributes', this.changedAttributes);
        setTimeout(async () => {
          await this.load();
          this.$emit('update:attributes', this.attributes);
        }, 200);
      }
      if (this.publicProfile !== state.profile.publicProfile) {
        await callApi('POST', '/v1/client/customer-flags', {
          flags: {
            private_profile: ! this.publicProfile,
          },
        });
        state.profile.publicProfile = this.publicProfile;
      }
    },
    async load () {
      const res = await callApi('GET', '/v1/client/my-customer-attributes');
      const json = await res.json();
      this.attributes = {...json.attributes};
      this.newAttributes = {...json.attributes};
      this.types = json.types;
    },
    openSkinSelector(ev) {
      if (this.hasNoSkin) {
        tooltip.create({ x: ev.pageX, y: ev.pageY }, this.t("has_no_skins"));
        return;
      }
      openProfileSkinSelectorModal();
    },
    openInModal,
    openAvatarConfigurator,
    openTokenShop,
    customizeAvatar() {
      if (this.use3DAvatar) {
        this.openAvatarConfigurator();
      } else {
        this.openInModal();
      }
    },
  },
  mounted () {
    if (! state.profile) {
      return;
    }
    this.load();
  },
  watch: {
    publicProfile (to, frm) {
      if (! this.autoSaveEnabled) {
        return;
      }
      if (to !== frm) {
        this.debouncedSave();
      }
    },
    'state.profile': function (to, frm) {
      if (to && ! frm) {
        this.load();
        this.publicProfile = state.profile?.publicProfile;
      }
    },
  }
};
</script>
